import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getCurrentSlice, isAppLang, reducerNameToPlatform } from 'utils';
import { applyFiltersToApiRequest, toggleSidebarFilters } from 'store';
import { useRouter } from 'hooks';
import {
  SubjectFilter,
  ResourceCategoryFilter,
  SentimentFilter,
  ContentTagFilter,
  AgeFilter,
  GenderFilter,
  ResourceTypeFilter,
  ResourcesFilter,
  ContentBasedFilter,
  AdvertFilter,
  MediaTypeFilter,
  ContentTypeFilter,
  ContentCategoryFilter,
  LanguageFilter,
  CountryFilter,
  PublishRateFilter,
  PhrasesFilter,
  PlatformFilter,
  SeenStatusFilter,
} from 'components/filters/components';
import AddFilterDrawer from './AddFilterDrawer';
import AddFilterDialog from './AddFilterDialog';

const mapToComponent = {
  subjectFilter: SubjectFilter,
  resourceCategoryFilter: ResourceCategoryFilter,
  contentTagFilter: ContentTagFilter,
  ageFilter: AgeFilter,
  genderFilter: GenderFilter,
  resourceTypeFilter: ResourceTypeFilter,
  resourcesFilter: ResourcesFilter,
  contentBasedFilter: ContentBasedFilter,
  mediaTypeFilter: MediaTypeFilter,
  contentTypeFilter: ContentTypeFilter,
  seenStatusFilter: SeenStatusFilter,
  publishRateFilter: PublishRateFilter,
  platformFilter: PlatformFilter,
};

if (!isAppLang('ar')) {
  mapToComponent.sentimentFilter = SentimentFilter;
  mapToComponent.contentCategoryFilter = ContentCategoryFilter;
  mapToComponent.advertFilter = AdvertFilter;
  mapToComponent.languageFilter = LanguageFilter;
  mapToComponent.countryFilter = CountryFilter;
}

let sliceModule = {};

function AddFilter({ allowedFilters }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const { clearRouterFilters } = useRouter();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const reducerName = useSelector(
    (state) => state.filterReducer.filtersContext
  );

  const settingsFilters = useSelector((state) => state.settingsReducer.filters);
  const settingsSortBy = useSelector((state) => state.settingsReducer.sortBy);

  useEffect(() => {
    if (reducerName) {
      sliceModule = getCurrentSlice(reducerName);
    }
  }, [reducerName]);

  useEffect(() => {
    if (!window.location.pathname.includes('statistics')) {
      mapToComponent.wordCloudFilter = PhrasesFilter;
      mapToComponent.hashtagCloudFilter = PhrasesFilter;
    } else {
      delete mapToComponent?.wordCloudFilter;
      delete mapToComponent?.hashtagCloudFilter;
    }
  }, []);

  const closeDialog = () => {
    dispatch(toggleSidebarFilters(false));
  };

  const handleApplyFilters = () => {
    dispatch(applyFiltersToApiRequest());
    closeDialog();
  };

  const handleResetFilters = () => {
    const platform = reducerNameToPlatform[reducerName];
    dispatch(sliceModule?.resetAllFilters());
    clearRouterFilters();
    if (
      location.pathname.includes('contents') ||
      location.pathname.includes('statistics')
    ) {
      dispatch(
        sliceModule?.applyWebsiteSettingsFilters({
          filters: settingsFilters[platform],
          sortBy: settingsSortBy[platform],
          filtersInitialState: sliceModule?.filtersInitialState,
        })
      );
    }

    handleApplyFilters();
  };

  if (isMobile) {
    return (
      <AddFilterDrawer
        mapToComponent={mapToComponent}
        sliceModule={sliceModule}
        onReset={handleResetFilters}
        onApply={handleApplyFilters}
        onClose={closeDialog}
        allowedFilters={allowedFilters}
      />
    );
  }

  return (
    <AddFilterDialog
      mapToComponent={mapToComponent}
      sliceModule={sliceModule}
      onReset={handleResetFilters}
      onApply={handleApplyFilters}
      onClose={closeDialog}
      allowedFilters={allowedFilters}
    />
  );
}

AddFilter.propTypes = {
  allowedFilters: PropTypes.arrayOf(PropTypes.string),
};

export default AddFilter;
